import React, { useState, useEffect } from 'react';
import { 
  QrCode, 
  Download, 
  Printer, 
  Eye, 
  X,
  FileText
} from 'lucide-react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import QRCodeJS from 'qrcode';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const QRPosterGenerator = ({ location, organization, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const generatePoster = async (action = 'download') => {
    setIsGenerating(true);
    
    try {
      // Create PDF med 200x300mm format som ønsket
      const pdf = new jsPDF({
        unit: 'mm',
        format: [200, 300], // Custom format: 200x300mm (ikke A4!)
        orientation: 'portrait',
        compress: true, // Aktiverer PDF-kompresjon
        precision: 2    // Reduserer presisjon for mindre filer
      });

      // Custom dimensjoner: 200x300mm
      const pageWidth = 200;  // 200mm bredde
      const pageHeight = 300; // 300mm høyde

      // Bruk bransjefargen for headeren (eller fallback til sort)
      const headerColor = location.discipline_color || location.discipline_color || '#1E283C'; // Bransje-spesifikk farge

      // 1. Header med bransje-spesifikk farge (justert for 200mm bredde)
      pdf.setFillColor(headerColor);
      pdf.roundedRect(15, 15, pageWidth - 30, 40, 5, 5, 'F'); // Mindre header for smal format

      // 2. "SKANN QR" tekst (hvit, stor, bold) - tilpasset smal format
      pdf.setTextColor(255, 255, 255); // Hvit tekst
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(28); // Mindre font for 200mm bredde
      const skannQrText = 'SKANN QR';
      const skannQrWidth = pdf.getTextWidth(skannQrText);
      pdf.text(skannQrText, (pageWidth - skannQrWidth) / 2, 35); // Justert posisjon

      // 3. "Registrer treningen" tekst (hvit, mindre) - justert for smal format
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14); // Mindre font for 200mm bredde
      const registrerText = 'Registrer treningen';
      const registrerWidth = pdf.getTextWidth(registrerText);
      pdf.text(registrerText, (pageWidth - registrerWidth) / 2, 45); // Justert posisjon

      // 4. Generer QR-kode som ren tekst (ikke URL)
      const qrCodeData = location.qr_code_data; // Ren UUID tekst, ikke URL!
      console.log('Generating QR with pure text:', qrCodeData);
      
      // Høy kvalitet QR-kode for 300 DPI printing
      const qrDataUrl = await QRCodeJS.toDataURL(qrCodeData, {
        width: 600, // Høy oppløsning for 300 DPI print
        margin: 2,
        color: {
          dark: '#000000', // 100% sort som ønsket
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M', // Medium error correction
        type: 'image/png' // PNG for beste kvalitet
      });

      // 5. Plasser QR-kode i midten (større for å fylle mer av bredden)
      const qrSize = 160; // mm - økt fra 120mm til 160mm for å fylle 80% av 200mm bredde
      const qrX = (pageWidth - qrSize) / 2;
      const qrY = 80; // Justert posisjon for større QR-kode
      
      // Bruk PNG for høyeste kvalitet (300 DPI)
      pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize, undefined, 'MEDIUM');

      // 6. Organisasjonsnavn øverst (svart tekst)
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      const orgName = organization.name;
      const orgWidth = pdf.getTextWidth(orgName);
      pdf.text(orgName, (pageWidth - orgWidth) / 2, 70);

      // 7. Discipline tag + Skytebanens navn under QR-kode
      const locationName = location.name;
      const disciplineTag = (location.discipline || '').toUpperCase();
      const disciplineColor = location.discipline_color || '#888888';
      const nameY = qrY + qrSize + 15;
      
      if (disciplineTag) {
        // 50% larger tag dimensions
        const tagFontSize = 21;  // 50% larger (was 14)
        const nameFontSize = 16;
        const lineSpacing = 6; // mm between lines
        
        // Measure tag text width
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(tagFontSize);
        const tagTextWidth = pdf.getTextWidth(disciplineTag);
        const tagPaddingX = 9;   // 50% larger
        const tagPaddingY = 3;   // 50% larger
        const tagWidth = tagTextWidth + tagPaddingX * 2;
        const tagHeight = tagFontSize * 0.4 + tagPaddingY * 2 + 4;
        const tagGap = 5;
        
        // Word-wrap the name
        const maxNameWidth = pageWidth * 0.55;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(nameFontSize);
        
        const words = locationName.split(' ');
        const nameLines = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? currentLine + ' ' + word : word;
          if (pdf.getTextWidth(testLine) > maxNameWidth && currentLine) {
            nameLines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) nameLines.push(currentLine);
        
        // Calculate widest line for centering
        const widestLine = Math.max(...nameLines.map(l => pdf.getTextWidth(l)));
        const totalWidth = tagWidth + tagGap + widestLine;
        const startX = (pageWidth - totalWidth) / 2;
        
        // Vertically center the tag with the name block
        const nameBlockHeight = (nameLines.length - 1) * lineSpacing;
        const tagCenterY = nameY + nameBlockHeight / 2;
        
        // Draw pill-shaped tag background
        const tagRadius = tagHeight / 2;
        const tagX = startX;
        const tagTopY = tagCenterY - tagHeight / 2;
        
        const hexToRgb = (hex) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return [r, g, b];
        };
        
        const [r, g, b] = hexToRgb(disciplineColor);
        pdf.setFillColor(r, g, b);
        pdf.roundedRect(tagX, tagTopY, tagWidth, tagHeight, tagRadius, tagRadius, 'F');
        
        // Draw tag text — use dark text on light backgrounds for readability
        const luminance = (r * 0.299 + g * 0.587 + b * 0.114);
        if (luminance > 160) {
          pdf.setTextColor(0, 0, 0);  // Black text on light backgrounds (yellow, white, etc.)
        } else {
          pdf.setTextColor(255, 255, 255);  // White text on dark backgrounds
        }
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(tagFontSize);
        pdf.text(disciplineTag, tagX + tagPaddingX, tagCenterY + tagFontSize * 0.15);
        
        // Draw wrapped location name lines
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(nameFontSize);
        const nameX = startX + tagWidth + tagGap;
        nameLines.forEach((line, i) => {
          pdf.text(line, nameX, nameY + i * lineSpacing);
        });
      } else {
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        const locationWidth = pdf.getTextWidth(locationName);
        pdf.text(locationName, (pageWidth - locationWidth) / 2, nameY);
      }

      // 8. Instruksjoner nederst (kompakt for smal format)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      const instructions = [
        '1. Åpne Aktivlogg på mobilen',
        '2. Gå til "Skann" siden',
        '3. Skann QR-koden for å registrere',
        '4. Treningsøkt registreres automatisk'
      ];
      
      let yPos = qrY + qrSize + 35;
      instructions.forEach(instruction => {
        const instWidth = pdf.getTextWidth(instruction);
        pdf.text(instruction, (pageWidth - instWidth) / 2, yPos);
        yPos += 10;
      });

      // Add Aktivlogg text branding at bottom center (4cm width as requested)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9); // Slightly larger for poster format
      pdf.setTextColor(0, 0, 0); // Black text
      
      const brandingText = 'AKTIVLOGG.no - digital treningslogg';
      const brandingWidth = pdf.getTextWidth(brandingText);
      const brandingX = (pageWidth - brandingWidth) / 2; // Center horizontally
      const brandingY = pageHeight - 12; // 12mm from bottom for 300mm height
      
      pdf.text(brandingText, brandingX, brandingY);

      if (action === 'preview') {
        // Åpne i nytt vindu
        const pdfBlob = pdf.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      } else if (action === 'download') {
        // Last ned PDF
        const filename = `qr-poster-${location.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
        pdf.save(filename);
      }
      
    } catch (error) {
      console.error('Poster generation error:', error);
      alert('Kunne ikke generere plakat. Prøv igjen.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Printer className="h-6 w-6 mr-2 text-yellow-500" />
            QR-kode Plakat Generator
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Preview Area - Eksakt som din mal */}
        <div className="bg-gray-100 rounded-lg p-8 mb-6 text-center">
          <div className="bg-white rounded-lg shadow-lg max-w-sm mx-auto overflow-hidden">
            
            {/* Sort header (som din mal) */}
            <div 
              className="px-6 py-8 text-white rounded-t-lg"
              style={{ backgroundColor: organization?.primary_color || '#1E283C' }}
            >
              <h1 className="text-3xl font-bold mb-2">SKANN QR</h1>
              <p className="text-lg">Registrer treningen</p>
            </div>
            
            {/* QR Code Placeholder */}
            <div className="p-8">
              <div className="bg-gray-200 w-32 h-32 mx-auto mb-6 rounded-lg flex items-center justify-center">
                <QrCode className="h-16 w-16 text-gray-400" />
              </div>
              
              {/* Location Name with Discipline Tag */}
              <div className="flex items-center justify-center gap-2.5">
                {location?.discipline && (
                  <span 
                    className="px-4 py-1.5 rounded-full text-white font-bold flex-shrink-0"
                    style={{ backgroundColor: location.discipline_color || '#888', fontSize: '0.95rem' }}
                  >
                    {location.discipline.toUpperCase()}
                  </span>
                )}
                <h2 className="text-xl font-medium text-gray-900 text-left leading-tight">
                  {location?.name || 'Skytebanens navn'}
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Info om QR-koden */}
        <div className="mb-6 bg-blue-600/20 border border-blue-600/50 rounded-lg p-4">
          <h3 className="text-blue-400 font-medium mb-2 flex items-center">
            <QrCode className="h-4 w-4 mr-2" />
            QR-kode informasjon
          </h3>
          <div className="text-blue-300 text-sm space-y-1">
            <p><strong>Bane:</strong> {location?.name}</p>
            <p><strong>QR-kode data:</strong> {location?.qr_code_data}</p>
            <p><strong>Format:</strong> Ren tekst (ikke URL)</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => generatePoster('preview')}
            disabled={isGenerating}
            className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
          >
            <Eye className="h-5 w-5 mr-2" />
            {isGenerating ? 'Genererer...' : 'Forhåndsvis'}
          </button>
          
          <button
            onClick={() => generatePoster('download')}
            disabled={isGenerating}
            className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 transition-colors disabled:opacity-50"
          >
            <Download className="h-5 w-5 mr-2" />
            {isGenerating ? 'Genererer...' : 'Last ned PDF'}
          </button>
        </div>

        {/* Info */}
        <div className="mt-6 bg-blue-600/20 border border-blue-600/50 rounded-lg p-4">
          <h3 className="text-blue-400 font-medium mb-2 flex items-center">
            <FileText className="h-4 w-4 mr-2" />
            Om A4 Plakaten
          </h3>
          <ul className="text-blue-300 text-sm space-y-1">
            <li>• Perfekt for utskrift på vanlig A4 papir</li>
            <li>• QR-koden inneholder ren tekst (UUID), ikke URL</li>
            <li>• Følger din eksakte mal design</li>
            <li>• Sort header med "SKANN QR" og skytebanens navn</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QRPosterGenerator;